/* eslint-disable @typescript-eslint/naming-convention */

export enum IFullDateFormat {
  h_mm_A = "LT",
  h_mm_ss_A = "LTS",
  MM_DD_YYYY = "L",
  MMMM_D_YYYY = "LL",
  MMMM_D_YYYY_h_mm_A = "LLL",
  dddd_MMMM_D_YYYY_h_mm_A = "LLLL",
}

export enum IAbbrevDateFormat {
  M_D_YYYY = "l",
  MMM_D = "MMM D",
  MMM_D_YYYY = "ll",
  MMM_D_YYYY_h_mm_A = "lll",
  ddd_MMM_D_YYYY_h_mm_A = "llll",
}

// Custom date usage for KW as defined in
// https://kazoohr.atlassian.net/browse/KAZ-5659
export type KazooDates =
  | "ANNOUNCEMENTS"
  | "ANNOUNCEMENTS_WITH_TIMEZONE"
  | "APPOINTMENT_RANGE"
  | "CARD"
  | "CARD_WITHOUT_YEAR"
  | "DATE_AT_TIME"
  | "STANDARD"
  | "STATUS"
  | "SYNC_UP_RANGE"
  | "SYNC_UP_RANGE_WITH_TIMEZONE"
  | "TABLE"
  | "TABLE_WITH_TIMEZONE"
  | "TABLE_WITH_COMPANY_TIMEZONE_APPLIED"
  | "UPDATE"
  /**
   * This is a special format equivalent to @see{UPDATE} that will
   * treat the specified date as if it were already in the
   * browser's/machine's local timezone. Use this for absolute date
   * values without a time —like a User's Employment Start Date—
   * which will get muddied up by shifting the timezone.
   */
  | "UPDATE_IGNORE_TIMEZONE"
  | "DAYS_UNTIL"
  | "MINUTES_UNTIL";

export type DaysDifferenceUnits =
  | "millisecond"
  | "second"
  | "minute"
  | "hour"
  | "day"
  | "month"
  | "year"
  | "date"
  | "milliseconds"
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "months"
  | "years"
  | "dates"
  | "d"
  | "D"
  | "M"
  | "y"
  | "h"
  | "m"
  | "s"
  | "ms"
  | "quarter"
  | "quarters"
  | "Q"
  | "week"
  | "weeks"
  | "w"
  | undefined;

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/* 
   This is a simple wrapper of dayjs that takes a date or date string and a more human readable enum 
    to internationalize dates. If format is KazooDate, it will use a custom format as defined in KAZ-5659.
*/
/* eslint-disable complexity */
export function i18nDate(
  date: Date | string | number | undefined,
  format: IFullDateFormat | IAbbrevDateFormat | KazooDates,
  endDate?: Date | string | number,
  timezone?: string
) {
  if (!date) {
    return "";
  }
  switch (format) {
    case "ANNOUNCEMENTS":
      // Aug 16, 2018 8:02pm
      return dayjs(date).format("lll");
    case "ANNOUNCEMENTS_WITH_TIMEZONE":
      // Aug 16, 2018 8:02pm CST
      return dayjs(date).format("lll z");
    case "APPOINTMENT_RANGE":
      // 10:30am - 11:30am | May 17, 2020
      if (endDate) {
        return `${dayjs(date).format("LT")} - ${dayjs(endDate).format(
          "LT"
        )} | ${dayjs(endDate).format("ll")}`;
      }
      // 10:30am | May 17, 2020
      return `${dayjs(date).format("LT")} | ${dayjs(date).format("ll")}`;
    case "CARD":
      // 10:37am | May 17, 2020
      return `${dayjs(date).format("LT")} | ${dayjs(date).format("ll")}`;
    case "CARD_WITHOUT_YEAR":
      // 11:12 pm CST | Jul 31
      return `${dayjs(date).format("LT z")} | ${dayjs(date).format("MMM D")}`;
    case "DAYS_UNTIL":
      // date1.diff('2018-06-05', 'month') // 7
      return dayjs(date).diff(new Date().toISOString(), "days").toString();
    case "MINUTES_UNTIL":
      return dayjs(date).diff(new Date().toISOString(), "minutes").toString();
    case "STANDARD":
      // 4/30/2020
      return dayjs(date).format("L");
    case "STATUS":
      // Mar 31, 2020
      return dayjs(date).format("ll");
    case "SYNC_UP_RANGE":
      // Sep 1, 2021 • 7:00 PM - 4:00 PM
      if (endDate) {
        return `${dayjs(endDate).format("ll")} • ${dayjs(date).format(
          "LT"
        )} - ${dayjs(endDate).format("LT")}`;
      }
      // Sep 1, 2021 • 7:00 PM
      return `${dayjs(date).format("ll")} • ${dayjs(date).format("LT")}`;
    case "SYNC_UP_RANGE_WITH_TIMEZONE":
      // Sep 1, 2021 • 7:00 PM - 4:00 PM CST
      return `${dayjs(endDate).format("ll")} • ${dayjs(date).format(
        "LT"
      )} - ${dayjs(endDate).format("LT z")}`;
    case "DATE_AT_TIME":
      // May 27, 2020 @ 11:37am CST
      return `${dayjs(date).format("ll")} @ ${dayjs(date).format("LT z")}`;
    case "TABLE":
      // May 27, 2020 | 11:37 AM
      return `${dayjs(date).format("ll")} | ${dayjs(date).format("LT")}`;
    case "TABLE_WITH_TIMEZONE":
      // May 27, 2020 | 11:37 AM CST
      return `${dayjs(date).format("ll")} | ${dayjs(date).format("LT z")}`;
    case "TABLE_WITH_COMPANY_TIMEZONE_APPLIED":
      // May 27, 2020 | 12:37 AM CST
      return `${dayjs(date).tz(timezone).format("ll")} | ${dayjs(date)
        .tz(timezone)
        .format("LT z")}`;
    case "UPDATE":
      // Nov 24, 2020
      return dayjs(date).format("ll");
    case "UPDATE_IGNORE_TIMEZONE":
      // Nov 24, 2020 - Does NOT shift the date to the browser's local time
      return dayjs.utc(date).format("ll");
    default:
      return dayjs(date).format(format);
  }
}
/* eslint-enable complexity */

export function differenceBetweenDates(
  startDate: string,
  endDate: string,
  unit: DaysDifferenceUnits
) {
  const startDateToDate = dayjs(startDate);
  const endDateToDate = dayjs(endDate);

  return endDateToDate.diff(startDateToDate, unit);
}

export function withinDateRange(
  relativeDate: Date | string,
  startDate?: Date | string,
  endDate?: Date | string
): boolean {
  const relativeDateToDate = dayjs(relativeDate);
  const startDateToDate = dayjs(startDate);
  const endDateToDate = dayjs(endDate);

  if (!endDate) {
    return startDateToDate.isBefore(relativeDateToDate);
  }

  return (
    startDateToDate.isBefore(relativeDateToDate) &&
    endDateToDate.isAfter(relativeDateToDate)
  );
}
